import { useState } from "react";
import { runForensicAnalysis } from "../context/functions/AdminFunction";
import { haveValue } from "../utils/common";

const ForensicAnalysis = (props) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);
  const [formattedResponse, setFormattedResponse] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setFileName(e.target.files[0].name);
      // Reset previous results when a new file is selected
      setRawResponse(null);
      setFormattedResponse(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Please select a file first.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append("file", file);

      // Make the API request
      const response = await runForensicAnalysis(formData);

      const data = response.success_res?.result?.analysis;
      if (haveValue(data)) {

        let analysisReport = data?.result ?? {};

        // Store the raw response
        setRawResponse(JSON.stringify(analysisReport, null, 2));

        const formatted = {
          verdict: analysisReport?.verdict || "-",
          score: analysisReport?.score || "-",
          documentType: analysisReport.documentType || "-",
          checks: analysisReport.checks || []
        };

        setFormattedResponse(formatted);

      } else {
        setRawResponse(null);
        setFormattedResponse(null);
      }
    } catch (err) {
      console.error("Error during API call:", err);
      setError(`Failed to analyze file: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fileUploadStyle = {
    border: "2px dashed #dee2e6",
    borderRadius: "4px",
    padding: "2rem",
    textAlign: "center",
    cursor: "pointer",
    marginBottom: "1rem"
  };

  const preStyle = {
    backgroundColor: "#212529",
    color: "#28a745",
    padding: "1rem",
    borderRadius: "4px",
    overflowX: "auto",
    fontSize: "0.875rem",
    whiteSpace: "pre-wrap"
  };

  return (
    <div className="container py-4">
      <h2 className="text-center mb-4">Forensic Analysis</h2>

      {/* File Upload Section */}
      <div className="card mb-4 create-admin-div w-100">
        <div className="card-body w-100">
          <div className="mb-3">
            <div
              style={fileUploadStyle}
              onClick={() => document.getElementById("fileInput").click()}
            >
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  fill="#6c757d"
                  className="bi bi-cloud-arrow-up"
                  viewBox="0 0 16 16"
                >
                  <path fillRule="evenodd" d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2z" />
                  <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383z" />
                </svg>
                <p className="mt-2">
                  {fileName ? `Selected: ${fileName}` : "Click to Upload a File"}
                </p>
              </div>
              <input
                id="fileInput"
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          </div>

          <button
            className={`btn ${isLoading || !file ? 'disabled' : ''} w-100`}
            disabled={isLoading || !file}
            onClick={handleSubmit}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              "Analyze File"
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Results Display */}
      {(rawResponse || formattedResponse) && (
        <>
          <h3 className="mb-3">Analysis Results</h3>

          <div className="row">

            {/* Formatted Response */}
            <div className="col-md-6 mb-4">
              <div className="card">
                <div className="card-header">Formatted Response</div>
                <div className="card-body">
                  {console.log(formattedResponse)}
                  {formattedResponse && (
                    <>
                      <h5 className="card-title">Verdict</h5>
                      <p className="card-text mb-3">{formattedResponse?.verdict}</p>

                      <h5 className="card-title">Score</h5>
                      <p className="card-text mb-3">{formattedResponse?.score}</p>

                      <h5 className="card-title">Document Type</h5>
                      <p className="card-text mb-3">{formattedResponse?.documentType}</p>

                      <h5 className="card-title">Checks Performated</h5>
                      {(formattedResponse?.checks ?? []).length > 0 ? (
                        <ul className="list-group list-group-flush mb-3 mt-4 ml-0">
                          {formattedResponse.checks.map((check, index) => (
                            <li className="list-group-item" key={index}>
                              <h6>{index + 1}. {check?.title} <strong>{check?.type}</strong></h6>
                              <p>{check?.remarks}</p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="card-text mb-3">No checks found</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Raw Response */}
            <div className="col-md-6 mb-4">
              <div className="card">
                <div className="card-body">
                  <img style={{ width: '100%' }} src="/images/7e81966c-5d0c-424c-82b7-8a339e7679e5.png" />
                </div>
              </div>
            </div>

          </div>

          <div className="row">
            <div className="col-md-12 mb-4">
              <div className="card">
                <div className="card-header">Raw Response</div>
                <div className="card-body">
                  <pre style={preStyle}>
                    {rawResponse}
                  </pre>
                </div>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default ForensicAnalysis;